/**
 * WorkspaceDataTable
 *
 * Thin rendering shell for TanStack Table instances.
 * Handles: header groups, row rendering (with selection state),
 * loading/empty fallback rows, and the pagination footer.
 *
 * Does NOT include the toolbar — feature table components own that.
 */
import { flexRender, type Table, type ColumnDef } from "@tanstack/react-table";
import { AlertCircleIcon, RotateCwIcon } from "lucide-react";

import { DataTablePagination } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table as UiTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type WorkspaceDataTableProps<TData> = {
  table: Table<TData>;
  /** Only used for colSpan calculation in loading/empty rows */
  columns: ColumnDef<TData>[];
  isLoading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  /** Plans 6.1: shared error state — distinct from empty; retry re-runs the query. */
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  isRefetching?: boolean;
  /**
   * When provided, the whole row becomes clickable (Strapi v5 style).
   * Cells with id "select" or "actions" call stopPropagation internally
   * so checkbox/action clicks don't trigger navigation.
   */
  onRowClick?: (row: TData) => void;
};

export function WorkspaceDataTable<TData>({
  table,
  columns,
  isLoading = false,
  emptyMessage = "Không có kết quả phù hợp.",
  isError = false,
  errorMessage = "Không tải được dữ liệu. Vui lòng thử lại.",
  onRetry,
  isRefetching = false,
  onRowClick,
}: WorkspaceDataTableProps<TData>) {
  const clickable = Boolean(onRowClick);

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-red-200 bg-card p-8 text-center dark:border-red-800">
        <AlertCircleIcon className="size-8 text-red-500" aria-hidden />
        <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry} disabled={isRefetching}>
            <RotateCwIcon className={`size-4 ${isRefetching ? "animate-spin" : ""}`} />
            Thử lại
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <UiTable>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b bg-muted/40 hover:bg-muted/40">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {Array.from({ length: Math.max(columns.length, 1) }).map((__, cellIndex) => (
                    <TableCell key={`skeleton-${rowIndex}-${cellIndex}`}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={clickable ? () => onRowClick!(row.original) : undefined}
                  className={clickable ? "cursor-pointer transition-colors hover:bg-muted/40" : "transition-colors hover:bg-muted/30"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      // Stop propagation for select/actions columns so
                      // checkbox and ⋮ clicks don't trigger row navigation
                      onClick={
                        clickable && (cell.column.id === "select" || cell.column.id === "actions")
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </UiTable>
      </div>

      <DataTablePagination table={table} className="mt-auto" />
    </div>
  );
}
